// OrganizationList.tsx - Enterprise Design (row-click + 3-dot menu)
import React, { useState, useMemo, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import './CompanyManagement.css';
import '../EnterpriseTable.css'; 
import './CompanyListOverrides.css';
import './CompanyHelpPanel.css';
import { ApiService } from './apiService';
import { Organization, OrganizationManagementProps } from './types';
import { ViewDetailsModal } from './ViewDetailsModal';
import { DeleteConfirmationModal } from './DeleteConfirmationModal';


interface OrganizationListProps extends OrganizationManagementProps {
  organizations: Organization[];
  isLoading: boolean;
  onEdit: (organization: Organization) => void;
  onCreateNew: () => void;
  onRefresh: () => void;
  onManageDivisions: (organization: Organization) => void;
  onManageUsers: (organization: Organization) => void;
}

interface DropdownState {
  isOpen: boolean;
  organizationId: string | null;
}

const OrgDropdownPortal: React.FC<{
  organization: Organization;
  position: { x: number; y: number };
  onViewDetails: (org: Organization) => void;
  onEdit: (org: Organization) => void;
  onManageDivisions: (org: Organization) => void;
  onManageUsers: (org: Organization) => void;
  onDelete: (org: Organization) => void;
  onClose: () => void;
}> = ({ organization, position, onViewDetails, onEdit, onManageDivisions, onManageUsers, onDelete, onClose }) => {
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        onClose();
      }
    };
    const timer = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 0);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onClose]);

  useEffect(() => {
    const handleScroll = () => onClose();
    window.addEventListener('scroll', handleScroll, true);
    return () => window.removeEventListener('scroll', handleScroll, true);
  }, [onClose]);

  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [onClose]);

  return ReactDOM.createPortal(
    <div ref={menuRef} className="gates-portal-dropdown-menu" style={{
      position: 'fixed',
      left: `${position.x}px`,
      top: `${position.y}px`,
    }}>
      <button onClick={() => onViewDetails(organization)} className="gates-portal-dropdown-item" type="button">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
        View Details
      </button>
      <button onClick={() => onEdit(organization)} className="gates-portal-dropdown-item" type="button">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
        Edit
      </button>
      <button onClick={() => onManageDivisions(organization)} className="gates-portal-dropdown-item" type="button">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/></svg>
        Manage Divisions
      </button>
      <button onClick={() => onManageUsers(organization)} className="gates-portal-dropdown-item" type="button">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
        Manage Users
      </button>
      <div className="gates-portal-dropdown-divider"></div>
      <button onClick={() => onDelete(organization)} className="gates-portal-dropdown-item delete" type="button">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
        Delete
      </button>
    </div>,
    document.body
  );
};

export const OrganizationList: React.FC<OrganizationListProps> = ({
  organizations,
  isLoading,
  getAccessToken,
  onStatusUpdate,
  onEdit,
  onCreateNew,
  onRefresh,
  onManageDivisions,
  onManageUsers
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [apiService] = useState(() => new ApiService(getAccessToken));
  const [dropdown, setDropdown] = useState<DropdownState>({ isOpen: false, organizationId: null });
  const [dropdownPosition, setDropdownPosition] = useState<{ x: number; y: number } | null>(null);
  const [activeOrganization, setActiveOrganization] = useState<Organization | null>(null);
  const [helpVisible, setHelpVisible] = useState(() => {
    try {
      const stored = localStorage.getItem('gateqx-help-visible-companies');
      return stored !== null ? stored === 'true' : true;
    } catch { return true; }
  });
  const [showActionHint, setShowActionHint] = useState(false);
  
  const [viewDetailsModal, setViewDetailsModal] = useState({
    isOpen: false,
    organization: null as Organization | null
  });

  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    organization: null as Organization | null
  });
  

  // Persist help guide visibility
  useEffect(() => {
    try { localStorage.setItem('gateqx-help-visible-companies', String(helpVisible)); } catch {}
  }, [helpVisible]);

  const filteredOrganizations = useMemo(() => {
    if (!searchTerm.trim()) {
      return organizations;
    }
    const term = searchTerm.toLowerCase().trim();
    return organizations.filter(org => 
      org.companyName.toLowerCase().includes(term)
    );
  }, [organizations, searchTerm]);

  // Auto-dismiss the action hint after 6 seconds, or when user opens any dropdown
  useEffect(() => {
    if (!showActionHint) return;
    const timer = setTimeout(() => setShowActionHint(false), 6000);
    return () => clearTimeout(timer);
  }, [showActionHint]);

  useEffect(() => {
    if (dropdown.isOpen) setShowActionHint(false);
  }, [dropdown.isOpen]);

  const toggleDropdown = (organization: Organization, event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent row click

    if (dropdown.isOpen && dropdown.organizationId === organization.companyId.toString()) {
      closeDropdown();
    } else {
      const rect = event.currentTarget.getBoundingClientRect();
      const dropdownWidth = 200;
      const viewportWidth = window.innerWidth;

      let xPos = rect.left - dropdownWidth + rect.width;
      if (xPos < 10) xPos = rect.left;
      if (xPos + dropdownWidth > viewportWidth - 10) xPos = viewportWidth - dropdownWidth - 10;

      setDropdown({ isOpen: true, organizationId: organization.companyId.toString() });
      setDropdownPosition({ x: xPos, y: rect.bottom + 5 });
      setActiveOrganization(organization);
    }
  };

  const closeDropdown = () => {
    setDropdown({ isOpen: false, organizationId: null });
    setDropdownPosition(null);
    setActiveOrganization(null);
  };

  const clearSearch = () => { setSearchTerm(''); };

  // Row click opens view details
  const handleRowClick = (organization: Organization) => {
    setViewDetailsModal({ isOpen: true, organization });
  };

  const handleCloseViewDetails = () => {
    setViewDetailsModal({ isOpen: false, organization: null });
  };

  const handleDelete = (organization: Organization) => {
    setDropdown({ isOpen: false, organizationId: null });
    setDeleteModal({ isOpen: true, organization });
  };

  const handleConfirmDelete = async () => {
    if (!deleteModal.organization) return;
    const result = await apiService.deleteOrganization(deleteModal.organization);
    setDeleteModal({ isOpen: false, organization: null });
    if (result.success) {
      onStatusUpdate({
        type: 'success',
        message: `The company "${deleteModal.organization.companyName}" has been successfully deleted from the system.`
      });
      onRefresh();
    } else {
      onStatusUpdate({
        type: 'error',
        message: result.message || 'Failed to delete organization'
      });
    }
  };

  const handleCancelDelete = () => {
    setDeleteModal({ isOpen: false, organization: null });
  };

  const handleEdit = (organization: Organization) => {
    setDropdown({ isOpen: false, organizationId: null });
    onEdit(organization);
  };

  const handleViewDetails = (organization: Organization) => {
    setDropdown({ isOpen: false, organizationId: null });
    setViewDetailsModal({ isOpen: true, organization });
  };

  const handleManageDivisions = (organization: Organization) => {
    setDropdown({ isOpen: false, organizationId: null });
    onManageDivisions(organization);
  };

  const handleManageUsers = (organization: Organization) => {
    setDropdown({ isOpen: false, organizationId: null });
    onManageUsers(organization);
  };

  if (isLoading) {
    return (
      <div className="loading">
        <span className="spinner"></span>
        Loading organizations...
      </div>
    );
  }

  if (organizations.length === 0) {
    return (
      <div className="company-management">
        <div className="empty-state">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
            <polyline points="9 22 9 12 15 12 15 22" />
          </svg>
          <h3>No Organizations Found</h3>
          <p>Start by adding your first organization to the system.</p>
          <button onClick={onCreateNew} className="create-first-btn">
            Add First Organization
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="company-management">
      <div className={`cm-layout ${!helpVisible ? 'help-collapsed' : ''}`}>
        {/* Main content */}
        <div className="cm-main">
         <h1 style={{
  fontSize: '25px',
  fontWeight: 600,
  color: '#1a1a1a',
  margin: '0 0 24px 0',
  padding: '5px 0 0 20px',   // ← align with toolbar content
  lineHeight: 1.3,
  fontFamily: "'Segoe UI', sans-serif",
  letterSpacing: '-0.01em',
}}>
  Company Management
</h1>
      <div className="companies-container" style={{ borderWidth: 0, borderStyle: 'none', boxShadow: 'none' }}>
        {/* Toolbar */}
        <div className="table-toolbar">
          <div className="toolbar-left">
            <span className="record-count">{organizations.length} organizations</span>
          </div>
          <div className="toolbar-right">
            <div className="search-field">
              <svg className="search-icon-svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
              <input
                type="text"
                placeholder="Search organizations..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
              />
              {searchTerm && (
                <button onClick={clearSearch} className="clear-search-btn" title="Clear search">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              )}
            </div>
            <button onClick={onCreateNew} className="create-company-btn">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="12" y1="5" x2="12" y2="19" />
                <line x1="5" y1="12" x2="19" y2="12" />
              </svg>
              <span>New Organization</span>
            </button>
          </div>
        </div>

        {searchTerm && (
          <div className="search-results-bar">
            Showing {filteredOrganizations.length} of {organizations.length} results for &ldquo;<strong>{searchTerm}</strong>&rdquo;
            <button onClick={clearSearch} className="clear-filter-btn">Clear filter</button>
          </div>
        )}

        <div className="companies-table-container">
          <table className="enterprise-table">
            <thead>
              <tr>
                <th className="th-name">Organization</th>
                <th className="th-regions">Regions</th>
                <th className="th-contact">Contact</th>
                <th className="th-status">Status</th>
                <th className="th-actions"></th>
              </tr>
            </thead>
            <tbody>
              {filteredOrganizations.length === 0 ? (
                <tr>
                  <td colSpan={5} className="no-results">
                    <div className="no-results-content">
                      <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="11" cy="11" r="8" />
                        <line x1="21" y1="21" x2="16.65" y2="16.65" />
                      </svg>
                      <p>No organizations found matching your search</p>
                      <button onClick={clearSearch} className="clear-search-link">Clear search</button>
                    </div>
                  </td>
                </tr>
              ) : (
                filteredOrganizations.map((organization, rowIndex) => (
                  <tr
                    key={organization.companyId}
                    className="clickable-row"
                    onClick={() => handleRowClick(organization)}
                    tabIndex={0}
                    onKeyDown={(e) => { if (e.key === 'Enter') handleRowClick(organization); }}
                  >
                    <td className="td-name">
                      <div className="name-details">
                        <span className="org-name-text">{organization.companyName}</span>
                      </div>
                    </td>
                    <td className="td-regions">
                      {organization.regions && organization.regions.length > 0 ? (
                        <div className="regions-container">
                          {organization.regions.map((region, index) => (
                            <span
                              key={`${region.regionId}-${index}`}
                              className={`region-chip ${region.authorized ? 'authorized' : 'default'}`}
                              title={region.authorized ? `${region.regionName} — Authorized` : region.regionName}
                            >
                              {region.regionCode}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span className="empty-field">&mdash;</span>
                      )}
                    </td>
                    <td className="td-contact">
                      <div className="contact-info">
                        <span className="contact-email">{organization.contactEmail}</span>
                        {organization.contactNumber && (
                          <span className="contact-phone">{organization.contactNumber}</span>
                        )}
                      </div>
                    </td>
                    <td className="td-status">
                      {organization.blacklisted ? (
                        <span className="status-pill blacklisted">
                          <span className="status-dot"></span>
                          Blacklisted
                        </span>
                      ) : (
                        <span className="status-pill active">
                          <span className="status-dot"></span>
                          Active
                        </span>
                      )}
                    </td>
                    <td className="td-actions" onClick={(e) => e.stopPropagation()}>
                      <div className="company-action-dropdown">
                        {/* First-time hint tooltip on the first row */}
                        {rowIndex === 0 && showActionHint && (
                          <div className="action-hint-tooltip" onClick={() => setShowActionHint(false)}>
                            <span>Click here to view, edit, or manage this company</span>
                            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                            </svg>
                          </div>
                        )}
                        <button
                          onClick={(e) => toggleDropdown(organization, e)}
                          className={`company-action-trigger ${rowIndex === 0 && showActionHint ? 'hint-pulse' : ''}`}
                          title="More actions"
                          type="button"
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                            <circle cx="12" cy="5" r="2" />
                            <circle cx="12" cy="12" r="2" />
                            <circle cx="12" cy="19" r="2" />
                          </svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
        </div>

        {/* Right: Help Guide — animated collapse */}
        <div
          className="cm-help-right"
          style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'flex-start',
            flexShrink: 0,
            position: 'sticky',
            top: 0,
            alignSelf: 'flex-start',
            width: helpVisible ? 310 : 44,
            padding: '28px 0 32px 0',
            transition: 'width 0.3s ease',
            overflow: 'hidden',
          }}
        >
          {/* Help content — always in DOM, fades via opacity for smooth transition */}
          <div
            className="cm-help-content"
            style={{
              flex: helpVisible ? 1 : '0 0 0px',
              opacity: helpVisible ? 1 : 0,
              overflow: 'hidden',
              transition: 'opacity 0.25s ease, flex 0.3s ease',
              pointerEvents: helpVisible ? 'auto' : 'none',
              minWidth: 0,
              padding: helpVisible ? '0 16px 0 28px' : '0',
            }}
          >
            <div className="cm-help-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
                <line x1="12" y1="17" x2="12.01" y2="17" />
              </svg>
            </div>
            <p className="cm-help-title">Company Management</p>
            <p className="cm-help-text">
              Manage all organizations that interact with your gate pass system. Each company can be assigned to multiple regions.
            </p>
            <p className="cm-help-subtitle">Divisions &amp; Users</p>
            <p className="cm-help-text">
              Each company can have multiple divisions, with approvers and reviewers configured at the division level. To assign users as approvers or reviewers, they must first be added to the company — use <strong>'Manage Users'</strong> from the three-dot menu to add them before configuring divisions.
            </p>
            <p className="cm-help-subtitle">Row &amp; Quick Actions</p>
            <p className="cm-help-text">
              Click any row to view full details with action buttons inside. Or use the three-dot menu for quick access to edit, manage divisions and users, or delete.
            </p>
            <p className="cm-help-subtitle">Region Chips</p>
            <p className="cm-help-text">
              Highlighted regions indicate authorized access. Grey regions are assigned but not yet authorized.
            </p>
          </div>

          {/* Toggle — vertical "Hide Guide" tab when expanded, circular ? icon when collapsed */}
          <button
            type="button"
            onClick={() => setHelpVisible(!helpVisible)}
            aria-label={helpVisible ? 'Hide Guide' : 'Show Guide'}
            title={helpVisible ? 'Hide Guide' : 'Show Guide'}
            style={helpVisible ? {
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              writingMode: 'vertical-rl',
              textOrientation: 'mixed',
              background: '#f5f5f5',
              border: '1px solid #e0e0e0',
              borderRight: 'none',
              borderRadius: '6px 0 0 6px',
              padding: '12px 7px',
              fontSize: 12,
              fontWeight: 600,
              color: '#374151',
              cursor: 'pointer',
              letterSpacing: '0.02em',
              flexShrink: 0,
              alignSelf: 'flex-start',
              marginTop: 36,
              zIndex: 2,
              transition: 'background 0.15s ease',
            } : {
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 34,
              height: 34,
              borderRadius: '50%',
              border: '1.5px solid #d4d4d4',
              background: '#ffffff',
              cursor: 'pointer',
              flexShrink: 0,
              margin: '6px auto 0 auto',
              padding: 0,
              transition: 'background 0.15s ease, border-color 0.15s ease',
            }}
            onMouseEnter={(e) => {
              if (helpVisible) {
                e.currentTarget.style.background = '#ebebeb';
              } else {
                e.currentTarget.style.borderColor = '#F3694A';
                e.currentTarget.style.background = '#fef7f5';
              }
            }}
            onMouseLeave={(e) => {
              if (helpVisible) {
                e.currentTarget.style.background = '#f5f5f5';
              } else {
                e.currentTarget.style.borderColor = '#d4d4d4';
                e.currentTarget.style.background = '#ffffff';
              }
            }}
          >
            {helpVisible ? (
              <>
                <svg
                  width="14" height="14" viewBox="0 0 24 24" fill="none"
                  stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                  style={{ flexShrink: 0, transform: 'rotate(90deg)' }}
                >
                  <circle cx="12" cy="12" r="10" />
                  <path d="M15 9l-6 3 6 3" />
                </svg>
                <span>Hide Guide</span>
              </>
            ) : (
              <svg
                width="18" height="18" viewBox="0 0 24 24" fill="none"
                stroke="#848d9a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
              >
                <circle cx="12" cy="12" r="10" />
                <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
                <line x1="12" y1="17" x2="12.01" y2="17" />
              </svg>
            )}
          </button>
        </div>
      </div>

      {/* Portal-rendered Dropdown */}
      {dropdown.isOpen && activeOrganization && dropdownPosition && (
        <OrgDropdownPortal
          organization={activeOrganization}
          position={dropdownPosition}
          onViewDetails={handleViewDetails}
          onEdit={handleEdit}
          onManageDivisions={handleManageDivisions}
          onManageUsers={handleManageUsers}
          onDelete={handleDelete}
          onClose={closeDropdown}
        />
      )}

      {/* View Details Modal with action buttons */}
      {viewDetailsModal.organization && (
        <ViewDetailsModal
          organization={viewDetailsModal.organization}
          isOpen={viewDetailsModal.isOpen}
          onClose={handleCloseViewDetails}
          onEdit={handleEdit}
          onManageDivisions={handleManageDivisions}
          onManageUsers={handleManageUsers}
          onDelete={handleDelete}
        />
      )}

      <DeleteConfirmationModal
        isOpen={deleteModal.isOpen}
        companyName={deleteModal.organization?.companyName || ''}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
      />
    </div>
  );
};